(() => {
  "use strict";

  const addModes = new Set(["ready_stock", "standard_mto"]);
  const inquiryModes = new Set(["personal_fit", "request_availability"]);
  const knownModes = new Set([...addModes, ...inquiryModes, "archive"]);
  const galleryRoles = new Set(["hero", "on-body", "exterior", "surface", "context"]);

  const cleanText = (value, maxLength = 160) => String(value || "")
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength);

  const normalizeId = (value) => cleanText(value, 96).split("/").pop().replace(/\D/g, "");
  const strictNumericId = (value) => {
    const candidate = cleanText(value, 96);
    return /^\d+$/.test(candidate) ? candidate : "";
  };

  const parseData = (root, selector) => {
    const node = root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || "null");
    } catch (error) {
      return null;
    }
  };

  const initializeProduct = (root) => {
    if (root.dataset.ensoInitialized === "true") return;
    root.dataset.ensoInitialized = "true";

    const data = parseData(root, "[data-enso-product-json]");
    const surfaces = Array.isArray(data?.surfaces)
      ? data.surfaces.map((surface) => ({
        id: normalizeId(surface?.id),
        name: cleanText(surface?.name, 120)
      })).filter((surface) => surface.id && surface.name)
      : [];
    const surfaceById = new Map(surfaces.map((surface) => [surface.id, surface]));
    const variants = Array.isArray(data?.variants)
      ? data.variants.map((variant) => {
        const rawPriceMinor = variant?.priceMinor;
        return {
          ...variant,
          id: normalizeId(variant?.id),
          surfaceId: normalizeId(variant?.surfaceId),
          surface: cleanText(variant?.surface, 120),
          size: cleanText(variant?.size, 80),
          fulfilmentMode: cleanText(variant?.fulfilmentMode, 40),
          availabilityLabel: cleanText(variant?.availabilityLabel, 160),
          leadTime: cleanText(variant?.leadTime, 120),
          price: cleanText(variant?.price, 80),
          priceMinor: rawPriceMinor === null || rawPriceMinor === undefined || rawPriceMinor === ""
            ? Number.NaN
            : Number(rawPriceMinor)
        };
      })
      : [];
    const productMediaIds = new Set(
      (Array.isArray(data?.productMediaIds) ? data.productMediaIds : []).map(normalizeId).filter(Boolean)
    );
    const labels = data?.labels || {};
    const surfaceRequired = Number(data?.surfacePosition) > 0;
    const sizeRequired = Number(data?.sizePosition) > 0;
    const sizeLabel = cleanText(data?.sizeOptionName || "Size", 80);
    const selectionNoun = /\bsize\b/i.test(sizeLabel) ? "size" : sizeLabel.toLocaleLowerCase();
    const ids = variants.map((variant) => variant.id).filter(Boolean);
    const contractIsValid = data?.contractValid === true
      && variants.length > 0
      && ids.length === variants.length
      && new Set(ids).size === ids.length
      && (!surfaceRequired || surfaces.length > 0)
      && variants.every((variant) => !surfaceRequired || surfaceById.has(variant.surfaceId));

    const normalizeMediaItem = (item) => {
      const mediaId = normalizeId(item?.mediaId);
      const role = cleanText(item?.role, 32);
      const src = cleanText(item?.src, 2048);
      if (!mediaId || !productMediaIds.has(mediaId) || !galleryRoles.has(role) || !src) return null;
      return {
        mediaId,
        role,
        label: cleanText(item?.label, 80) || role,
        alt: cleanText(item?.alt, 240),
        src,
        fallbackSrc: cleanText(item?.fallbackSrc, 2048),
        srcset: cleanText(item?.srcset, 6000),
        sizes: cleanText(item?.sizes, 600)
      };
    };

    const runtimeGalleries = new Map();
    (Array.isArray(data?.galleries) ? data.galleries : []).forEach((entry) => {
      const surfaceId = normalizeId(entry?.surfaceId);
      if (!surfaceById.has(surfaceId)) return;
      const source = entry?.source === "metaobject" ? "metaobject" : "legacy";
      const rawItems = Array.isArray(entry?.items) ? entry.items : [];
      const seen = new Set();
      let items = rawItems.map(normalizeMediaItem).filter((item) => {
        if (!item || seen.has(item.mediaId)) return false;
        seen.add(item.mediaId);
        return true;
      }).slice(0, 5);
      const fallbackHero = normalizeMediaItem(entry?.fallbackHero);
      const metaContractValid = source !== "metaobject"
        || (entry?.contractValid === true && items[0]?.role === "hero");
      if (!metaContractValid) items = [];
      if (items[0]?.role !== "hero" && fallbackHero && !seen.has(fallbackHero.mediaId)) {
        items.unshift(fallbackHero);
      }
      if (!items.length && fallbackHero) items = [fallbackHero];
      runtimeGalleries.set(surfaceId, {
        surfaceId,
        surface: surfaceById.get(surfaceId)?.name || cleanText(entry?.surface, 120),
        source,
        items: items.slice(0, 5),
        fallbackHero,
        broken: new Set()
      });
    });

    const sizeList = root.querySelector("[data-enso-size-options] .enso-object-product__size-list");
    if (sizeList) {
      const numericCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
      [...sizeList.children]
        .sort((left, right) => numericCollator.compare(
          left.querySelector("input")?.value || "",
          right.querySelector("input")?.value || ""
        ))
        .forEach((item) => sizeList.append(item));
    }

    const surfaceInputs = [...root.querySelectorAll("[data-enso-surface-options] input[type='radio']")];
    const sizeInputs = [...root.querySelectorAll("[data-enso-size-options] input[type='radio']")];
    const fitInputs = [...root.querySelectorAll("[data-enso-fit-options] input[type='radio']")];
    const surfaceCards = [...root.querySelectorAll("[data-enso-surface-card]")];
    const fitOptions = root.querySelector("[data-enso-fit-options]");
    const sizeOptions = root.querySelector("[data-enso-size-options]");
    const fitSummary = root.querySelector("[data-enso-fit-summary]");
    const price = root.querySelector("[data-enso-product-price]");
    const confirmation = root.querySelector("[data-enso-confirmation]");
    const form = root.querySelector(".enso-object-product__form");
    const variantInput = root.querySelector("[data-enso-variant-id]");
    const leadProperty = root.querySelector("[data-enso-lead-property]");
    const addAction = root.querySelector("[data-enso-add]");
    const addLabel = root.querySelector("[data-enso-add-label]");
    const inquiryAction = root.querySelector("[data-enso-inquiry-action]");
    const inquiryLabel = root.querySelector("[data-enso-inquiry-label]");
    const actionNote = root.querySelector("[data-enso-action-note]");
    const productError = root.querySelector("[data-enso-product-error]");
    const facts = root.querySelector("[data-enso-product-facts]");
    const factAvailabilityLabel = root.querySelector("[data-enso-fact-availability-label]");
    const factAvailability = root.querySelector("[data-enso-fact-availability]");
    const copyContextButton = root.querySelector("[data-enso-copy-context]");
    const copyStatus = root.querySelector("[data-enso-copy-status]");
    const manualContext = root.querySelector("[data-enso-manual-context]");
    const whatsappAction = root.querySelector("[data-enso-whatsapp]");
    const guidanceInquiryAction = root.querySelector("[data-enso-guidance-inquiry]");
    const policyDialog = root.querySelector("[data-enso-policy-dialog]");
    const policyTitle = root.querySelector("[data-enso-policy-title]");
    const policyBody = root.querySelector("[data-enso-policy-body]");
    const policyFullLink = root.querySelector("[data-enso-policy-full-link]");
    const policyClose = root.querySelector("[data-enso-policy-close]");
    const policyTriggers = [...root.querySelectorAll("[data-enso-policy-trigger]")];
    const policyTemplates = new Map(
      [...root.querySelectorAll("template[data-enso-policy-template]")]
        .map((template) => [cleanText(template.dataset.ensoPolicyTemplate, 32), template])
        .filter(([key]) => key)
    );
    const primaryMedia = root.querySelector("[data-enso-primary-media]");
    let primaryImage = primaryMedia?.querySelector("img") || null;
    let primaryImageReady = Boolean(primaryImage?.complete && primaryImage.naturalWidth > 0);
    const primaryFallback = primaryMedia?.querySelector("[data-enso-media-fallback]");
    const galleryControls = root.querySelector("[data-enso-gallery-controls]");
    const galleryPrevious = root.querySelector("[data-enso-gallery-prev]");
    const galleryNext = root.querySelector("[data-enso-gallery-next]");
    const galleryIndex = root.querySelector("[data-enso-gallery-index]");
    const galleryCount = root.querySelector("[data-enso-gallery-count]");
    const galleryCaption = root.querySelector("[data-enso-gallery-caption]");
    const galleryStatus = root.querySelector("[data-enso-gallery-status]");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    const variantsForSurface = (surfaceId) => variants.filter((variant) => (
      !surfaceRequired || variant.surfaceId === surfaceId
    ));

    const candidatesForTuple = (surfaceId, size) => variants.filter((variant) => (
      (!surfaceRequired || variant.surfaceId === surfaceId)
      && (!sizeRequired || variant.size === size)
    ));

    const exactVariant = (surfaceId, size) => {
      const candidates = candidatesForTuple(surfaceId, size);
      return candidates.length === 1 && candidates[0].id ? candidates[0] : null;
    };

    const variantHasUniqueTuple = (variant) => Boolean(
      variant?.id && exactVariant(variant.surfaceId, variant.size)?.id === variant.id
    );

    const firstValidSurfaceId = () => surfaces.find((surface) => (
      variantsForSurface(surface.id).some(variantHasUniqueTuple)
    ))?.id || "";

    const stateFromLocation = () => {
      const url = new URL(window.location.href);
      const requestedValues = url.searchParams.getAll("variant");
      const requestedId = requestedValues.length === 1 ? strictNumericId(requestedValues[0]) : "";
      const requestedVariants = requestedId
        ? variants.filter((variant) => variant.id === requestedId && variantHasUniqueTuple(variant))
        : [];
      if (requestedVariants.length === 1) {
        return {
          surfaceId: requestedVariants[0].surfaceId,
          size: requestedVariants[0].size,
          exact: requestedVariants[0]
        };
      }
      const configuredDefault = normalizeId(data?.defaultSurfaceId);
      const defaultSurfaceId = surfaceById.has(configuredDefault)
        && variantsForSurface(configuredDefault).some(variantHasUniqueTuple)
        ? configuredDefault
        : firstValidSurfaceId();
      return { surfaceId: defaultSurfaceId, size: "", exact: null };
    };

    const initialState = stateFromLocation();
    let selectedSurfaceId = initialState.surfaceId;
    let selectedSize = initialState.size;
    let selectedFit = fitInputs.find((input) => input.checked)?.value || "";
    let lastExact = initialState.exact;
    let mediaGeneration = 0;
    let currentGallerySurfaceId = selectedSurfaceId;
    let currentGalleryItemKey = normalizeId(primaryMedia?.dataset.ensoCurrentMediaId);
    let currentGalleryPosition = 0;
    let touchStart = null;

    const setError = (message = "") => {
      if (!productError) return;
      productError.textContent = message;
      productError.hidden = !message;
    };

    const priceForSurface = (surfaceId, size = "") => {
      if (size) {
        const exact = exactVariant(surfaceId, size);
        return exact?.price || "";
      }
      const group = variantsForSurface(surfaceId)
        .filter((variant) => Number.isFinite(variant.priceMinor) && variant.price)
        .sort((left, right) => left.priceMinor - right.priceMinor);
      if (!group.length) return "";
      return group[0].priceMinor === group[group.length - 1].priceMinor
        ? group[0].price
        : `FROM ${group[0].price}`;
    };

    const setSelectedStates = () => {
      surfaceInputs.forEach((input) => {
        const isSelected = normalizeId(input.dataset.ensoSurfaceId) === selectedSurfaceId;
        input.checked = isSelected;
        input.closest("[data-enso-surface-card]")?.setAttribute("data-selected", String(isSelected));
      });
      sizeInputs.forEach((input) => {
        const isSelected = input.value === selectedSize;
        input.checked = isSelected;
        input.closest("label")?.toggleAttribute("data-selected", isSelected);
      });
      fitInputs.forEach((input) => {
        const isSelected = input.value === selectedFit;
        input.checked = isSelected;
        input.closest("label")?.toggleAttribute("data-selected", isSelected);
      });
    };

    const syncSizeOptions = () => {
      sizeInputs.forEach((input) => {
        const exists = Boolean(exactVariant(selectedSurfaceId, input.value));
        input.disabled = !exists;
        const label = input.closest("label");
        label?.toggleAttribute("data-unavailable", !exists);
        if (label) label.hidden = !exists;
      });
      if (selectedSize && !exactVariant(selectedSurfaceId, selectedSize)) selectedSize = "";
      if (sizeOptions) sizeOptions.hidden = surfaceRequired && !selectedSurfaceId;
    };

    const renderSurfacePrices = () => {
      surfaceCards.forEach((card) => {
        const surfaceId = normalizeId(card.dataset.ensoSurfaceId);
        const output = card.querySelector("[data-enso-surface-price]");
        if (!output) return;
        output.textContent = priceForSurface(surfaceId, selectedSize);
        output.hidden = !output.textContent;
      });
    };

    const updateAddress = (variant) => {
      if (!window.history?.replaceState) return;
      const url = new URL(window.location.href);
      if (variant?.id) url.searchParams.set("variant", variant.id);
      else url.searchParams.delete("variant");
      window.history.replaceState(window.history.state, "", url);
    };

    const sameOriginSource = (variant = null) => {
      const source = new URL(window.location.href);
      source.hash = "";
      if (variant?.id) source.searchParams.set("variant", variant.id);
      else source.searchParams.delete("variant");
      return source.href;
    };

    const inquiryHref = (variant, need) => {
      if (!variant?.id || !inquiryModes.has(need)) return data.inquiryUrl;
      const url = new URL(data.inquiryUrl, window.location.origin);
      url.searchParams.set("object", cleanText(data.publicName, 120));
      url.searchParams.set("handle", cleanText(data.productHandle, 120));
      if (selectedSurfaceId) url.searchParams.set("surface", surfaceById.get(selectedSurfaceId)?.name || "");
      if (selectedSize) {
        url.searchParams.set("size", cleanText(selectedSize, 80));
        url.searchParams.set("size_label", sizeLabel);
      }
      if (selectedFit) url.searchParams.set("fit", cleanText(selectedFit, 120));
      url.searchParams.set("variant", variant.id);
      url.searchParams.set("need", need);
      url.searchParams.set("mode", cleanText(variant.fulfilmentMode, 40));
      url.searchParams.set("surface_required", surfaceRequired ? "1" : "0");
      url.searchParams.set("size_required", sizeRequired ? "1" : "0");
      url.searchParams.set("source", sameOriginSource(variant));
      return url.href;
    };

    const guidanceInquiryHref = (variant = null) => {
      const url = new URL(data.inquiryUrl, window.location.origin);
      url.searchParams.set("need", "configuration_guidance");
      url.searchParams.set("object", cleanText(data.publicName, 120));
      url.searchParams.set("handle", cleanText(data.productHandle, 120));
      const surfaceName = surfaceById.get(selectedSurfaceId)?.name || "";
      if (surfaceName) url.searchParams.set("surface", surfaceName);
      if (selectedSize) {
        url.searchParams.set("size", cleanText(selectedSize, 80));
        url.searchParams.set("size_label", sizeLabel);
      }
      if (selectedFit) url.searchParams.set("fit", cleanText(selectedFit, 120));
      if (variant?.id) {
        url.searchParams.set("variant", variant.id);
        url.searchParams.set("mode", cleanText(variant.fulfilmentMode, 40));
      }
      url.searchParams.set("surface_required", surfaceRequired ? "1" : "0");
      url.searchParams.set("size_required", sizeRequired ? "1" : "0");
      url.searchParams.set("source", sameOriginSource(variant));
      return url.href;
    };

    const resetActions = () => {
      if (variantInput) variantInput.value = "";
      if (leadProperty) {
        leadProperty.value = "";
        leadProperty.disabled = true;
      }
      if (addAction) {
        addAction.hidden = false;
        addAction.disabled = true;
      }
      if (inquiryAction) inquiryAction.hidden = true;
    };

    const deriveCommerceState = () => {
      if (!contractIsValid) {
        return {
          exact: null,
          action: "unavailable",
          label: labels.unavailable || "CONFIGURATION UNAVAILABLE",
          note: "Product option or variant data is incomplete. Acquisition remains unavailable."
        };
      }
      if (surfaceRequired && !selectedSurfaceId) {
        return {
          exact: null,
          action: "select",
          label: labels.selectSurface || "SELECT A SURFACE",
          note: "Select a surface to continue."
        };
      }
      if (sizeRequired && !selectedSize) {
        const configuredLabel = cleanText(labels.selectSize, 120);
        const defaultLabel = `SELECT A ${selectionNoun.toLocaleUpperCase()}`;
        return {
          exact: null,
          action: "select",
          label: configuredLabel && configuredLabel !== "SELECT A SIZE" ? configuredLabel : defaultLabel,
          note: `Choose a ${selectionNoun} to continue.`
        };
      }

      const exact = exactVariant(selectedSurfaceId, selectedSize);
      if (!exact?.id) {
        return {
          exact: null,
          action: "unavailable",
          label: labels.unavailable || "CONFIGURATION UNAVAILABLE",
          note: "The selected options do not resolve to one exact variant."
        };
      }
      const unavailable = (note) => ({
        exact,
        action: "unavailable",
        label: labels.unavailable || "CONFIGURATION UNAVAILABLE",
        note
      });
      if (!knownModes.has(exact.fulfilmentMode)) {
        return unavailable("Fulfilment data is incomplete for this configuration.");
      }
      if (exact.sizeRequired === true && !sizeRequired) {
        return unavailable("A required size option is not configured for this product.");
      }
      if (exact.fulfilmentMode === "archive") {
        return unavailable("This configuration is view only.");
      }
      if (inquiryModes.has(exact.fulfilmentMode)) {
        if (exact.inquiryContractValid !== true) {
          return unavailable("Inquiry availability data is incomplete for this configuration.");
        }
        return {
          exact,
          action: "inquiry",
          need: exact.fulfilmentMode,
          label: exact.fulfilmentMode === "personal_fit"
            ? (labels.personalFit || "BEGIN A PERSONAL-FIT INQUIRY")
            : (labels.request || "REQUEST AVAILABILITY"),
          note: exact.availabilityLabel || (exact.fulfilmentMode === "personal_fit"
            ? "Personal fit"
            : "Availability confirmed through inquiry"),
          availabilityLabel: "Availability",
          availability: exact.availabilityLabel || "Confirmed through inquiry"
        };
      }
      if (!addModes.has(exact.fulfilmentMode)) {
        return unavailable("This configuration is not available for acquisition.");
      }
      if (exact.commerceApproved !== true || !exact.availabilityLabel) {
        return unavailable("Commerce approval or availability data is incomplete for this configuration.");
      }
      if (exact.fulfilmentMode === "ready_stock") {
        if (exact.readyContractValid !== true) {
          return unavailable("Ready-stock inventory data is incomplete for this configuration.");
        }
        if (exact.readyInStock === true && exact.available === true) {
          return {
            exact,
            action: "add",
            label: labels.add || "ADD TO BAG",
            note: exact.availabilityLabel,
            availabilityLabel: "Availability",
            availability: exact.availabilityLabel
          };
        }
        if (exact.readyUnavailableValid === true && exact.available === false) {
          return {
            exact,
            action: "inquiry",
            need: "request_availability",
            label: labels.request || "REQUEST AVAILABILITY",
            note: "This ready-stock configuration is not currently available.",
            availabilityLabel: "Availability",
            availability: "Request availability"
          };
        }
        return unavailable("Ready-stock availability data is contradictory for this configuration.");
      }
      if (!exact.leadTime || exact.mtoContractValid !== true) {
        return unavailable("Made-to-order lead time or inventory policy is incomplete for this configuration.");
      }
      if (exact.available === true) {
        return {
          exact,
          action: "add",
          label: labels.add || "ADD TO BAG",
          note: [exact.availabilityLabel, exact.leadTime].filter(Boolean).join(" · "),
          availabilityLabel: "Lead time",
          availability: exact.leadTime
        };
      }
      return {
        exact,
        action: "inquiry",
        need: "request_availability",
        label: labels.request || "REQUEST AVAILABILITY",
        note: "This configuration is not currently available to add.",
        availabilityLabel: "Availability",
        availability: "Request availability"
      };
    };

    const renderFacts = (state) => {
      if (!facts || !factAvailabilityLabel || !factAvailability) return;
      const show = Boolean(state?.exact && state.availability);
      facts.hidden = !show;
      if (!show) return;
      factAvailabilityLabel.textContent = state.availabilityLabel || "Availability";
      factAvailability.textContent = state.availability;
    };

    const renderConfirmation = (exact) => {
      if (!confirmation) return;
      if (!exact?.id) {
        confirmation.textContent = "";
        confirmation.hidden = true;
        return;
      }
      const parts = [surfaceById.get(selectedSurfaceId)?.name || exact.surface];
      if (selectedSize) parts.push(`${sizeLabel.toLocaleUpperCase()} ${selectedSize}`);
      else if (exact.fulfilmentMode === "personal_fit") parts.push(selectedFit || "PERSONAL FIT");
      parts.push(exact.price);
      confirmation.textContent = parts.filter(Boolean).join(" · ");
      confirmation.hidden = false;
    };

    const contextText = (exact) => {
      const surfaceName = surfaceById.get(selectedSurfaceId)?.name || "Not selected";
      const lines = [
        "ENSO OBJECT QUESTION",
        `Object: ${cleanText(data.publicName, 120)}`,
        `Surface: ${surfaceName}`
      ];
      if (selectedSize) lines.push(`${sizeLabel}: ${selectedSize}`);
      if (selectedFit) lines.push(`Fit: ${selectedFit}`);
      const currentPrice = exact?.price || priceForSurface(selectedSurfaceId, selectedSize);
      if (currentPrice) lines.push(`Price: ${currentPrice}`);
      lines.push(`Link: ${sameOriginSource(exact)}`);
      return lines.join("\n");
    };

    const configureWhatsapp = () => {
      if (!whatsappAction) return;
      try {
        const url = new URL(data?.whatsappUrl || "");
        const isVerifiedShortLink = url.protocol === "https:"
          && url.hostname === "wa.me"
          && /^\/message\/[A-Z0-9]+\/?$/i.test(url.pathname);
        if (!isVerifiedShortLink) throw new Error("Invalid WhatsApp link.");
        whatsappAction.href = url.href;
        whatsappAction.hidden = true;
        whatsappAction.dataset.ensoWhatsappValid = "true";
      } catch (error) {
        whatsappAction.hidden = true;
        whatsappAction.removeAttribute("href");
        delete whatsappAction.dataset.ensoWhatsappValid;
      }
    };

    let policyReturnFocus = null;
    const releasePolicyLock = () => {
      if (!document.querySelector("[data-enso-policy-dialog][open]")) {
        document.documentElement.classList.remove("enso-policy-dialog-open");
      }
    };

    const closePolicy = () => {
      if (policyDialog?.open) policyDialog.close();
    };

    const openPolicy = (event) => {
      if (!policyDialog || typeof policyDialog.showModal !== "function" || !policyTitle || !policyBody) return;
      const trigger = event.currentTarget;
      const key = cleanText(trigger?.dataset.ensoPolicyTrigger, 32);
      const template = policyTemplates.get(key);
      if (!template?.content) return;

      let policyUrl;
      try {
        policyUrl = new URL(template.dataset.ensoPolicyUrl || trigger.href, window.location.origin);
        if (policyUrl.origin !== window.location.origin) return;
      } catch (error) {
        return;
      }

      policyReturnFocus = trigger;
      policyTitle.textContent = template.dataset.ensoPolicyTitle || "Policy";
      policyBody.replaceChildren(template.content.cloneNode(true));
      if (policyFullLink) policyFullLink.href = policyUrl.href;
      document.documentElement.classList.add("enso-policy-dialog-open");

      try {
        policyDialog.showModal();
        event.preventDefault();
        policyClose?.focus({ preventScroll: true });
      } catch (error) {
        policyBody.replaceChildren();
        releasePolicyLock();
        policyReturnFocus = null;
      }
    };

    policyTriggers.forEach((trigger) => trigger.addEventListener("click", openPolicy));
    policyClose?.addEventListener("click", closePolicy);
    policyDialog?.addEventListener("click", (event) => {
      if (event.target !== policyDialog) return;
      const bounds = policyDialog.getBoundingClientRect();
      const outsideSurface = event.clientX < bounds.left
        || event.clientX > bounds.right
        || event.clientY < bounds.top
        || event.clientY > bounds.bottom;
      if (outsideSurface) closePolicy();
    });
    policyDialog?.addEventListener("close", () => {
      policyBody?.replaceChildren();
      releasePolicyLock();
      if (policyReturnFocus?.isConnected) policyReturnFocus.focus({ preventScroll: true });
      policyReturnFocus = null;
    });

    const commitCommerce = () => {
      setError("");
      syncSizeOptions();
      setSelectedStates();
      renderSurfacePrices();
      if (fitOptions) fitOptions.hidden = true;
      if (fitSummary) {
        fitSummary.hidden = true;
        fitSummary.textContent = "";
      }

      const state = deriveCommerceState();
      lastExact = state.exact;
      const priceText = state.exact?.price || priceForSurface(selectedSurfaceId, selectedSize);
      if (price) {
        price.textContent = priceText;
        price.hidden = !priceText;
      }
      renderConfirmation(state.exact);
      renderFacts(state);
      updateAddress(state.exact);
      resetActions();

      const fitMode = state.exact?.fulfilmentMode === "personal_fit";
      if (fitOptions) fitOptions.hidden = !fitMode;
      if (fitSummary && state.exact) {
        fitSummary.hidden = false;
        fitSummary.textContent = fitMode
          ? "Fit and final specification are confirmed personally."
          : "This object is offered in one size.";
      }

      if (state.action === "add") {
        if (variantInput) variantInput.value = state.exact.id;
        if (addAction) addAction.disabled = false;
        if (leadProperty && state.exact.fulfilmentMode === "standard_mto") {
          leadProperty.value = state.exact.leadTime;
          leadProperty.disabled = false;
        }
        if (addLabel) addLabel.textContent = state.label;
      } else if (state.action === "inquiry") {
        if (addAction) addAction.hidden = true;
        if (inquiryAction) {
          inquiryAction.hidden = false;
          inquiryAction.href = inquiryHref(state.exact, state.need);
        }
        if (inquiryLabel) inquiryLabel.textContent = state.label;
      } else {
        if (addLabel) addLabel.textContent = state.label;
      }
      if (actionNote) actionNote.textContent = state.note || "";
      if (guidanceInquiryAction) guidanceInquiryAction.href = guidanceInquiryHref(state.exact);
      if (copyContextButton) copyContextButton.dataset.ensoCopyValue = contextText(state.exact);
      if (whatsappAction?.dataset.ensoWhatsappValid === "true") whatsappAction.hidden = true;
      if (manualContext) {
        manualContext.hidden = true;
        manualContext.value = "";
      }
      if (copyStatus) copyStatus.textContent = "";
    };

    const visibleGalleryItems = (gallery) => {
      if (!gallery) return [];
      const items = gallery.items.filter((item) => !gallery.broken.has(item.mediaId));
      const hasHero = items.some((item) => item.role === "hero");
      const fallback = gallery.fallbackHero;
      if (!hasHero && fallback && !gallery.broken.has(fallback.mediaId)
        && !items.some((item) => item.mediaId === fallback.mediaId)) {
        items.unshift(fallback);
      }
      return items.slice(0, 5);
    };

    const updateGalleryMeta = (gallery, item, position, announce = true) => {
      const items = visibleGalleryItems(gallery);
      const count = items.length;
      const safePosition = count ? Math.max(0, Math.min(position, count - 1)) : 0;
      if (galleryCaption) galleryCaption.textContent = item?.label || "";
      if (galleryIndex) galleryIndex.textContent = String(count ? safePosition + 1 : 0).padStart(2, "0");
      if (galleryCount) galleryCount.textContent = String(count).padStart(2, "0");
      if (galleryControls) galleryControls.hidden = count <= 1;
      if (galleryPrevious) galleryPrevious.disabled = count <= 1;
      if (galleryNext) galleryNext.disabled = count <= 1;
      if (announce && galleryStatus && item) {
        galleryStatus.textContent = `${gallery.surface} · ${item.label} · ${safePosition + 1} of ${count}`;
      }
    };

    const decodeImage = async (image) => {
      if (typeof image.decode === "function") await image.decode();
      else if (!image.complete || image.naturalWidth === 0) {
        await new Promise((resolve, reject) => {
          image.addEventListener("load", resolve, { once: true });
          image.addEventListener("error", reject, { once: true });
        });
      }
      if (!image.naturalWidth) throw new Error("Image did not decode.");
      return image;
    };

    const loadMediaItem = async (item) => {
      const makeImage = (source, useResponsive) => {
        const image = new Image();
        image.alt = item.alt || `${cleanText(data.publicName, 120)} · ${item.label}`;
        image.decoding = "async";
        image.loading = "eager";
        if (useResponsive && item.sizes) image.sizes = item.sizes;
        if (useResponsive && item.srcset) image.srcset = item.srcset;
        image.src = source;
        return image;
      };
      try {
        return await decodeImage(makeImage(item.src, true));
      } catch (error) {
        if (!item.fallbackSrc || item.fallbackSrc === item.src) return null;
        try {
          return await decodeImage(makeImage(item.fallbackSrc, false));
        } catch (fallbackError) {
          return null;
        }
      }
    };

    const commitNeutralMedia = (surfaceId, generation) => {
      if (generation !== mediaGeneration || surfaceId !== selectedSurfaceId || !primaryMedia) return;
      primaryImage?.remove();
      primaryImage = null;
      primaryImageReady = false;
      currentGallerySurfaceId = surfaceId;
      currentGalleryItemKey = "";
      currentGalleryPosition = 0;
      primaryMedia.dataset.ensoCurrentMediaId = "";
      primaryMedia.classList.add("enso-object-product__media--empty");
      if (primaryFallback) primaryFallback.hidden = false;
      updateGalleryMeta(runtimeGalleries.get(surfaceId), null, 0, false);
    };

    const commitMedia = (surfaceId, item, position, image, generation) => {
      if (generation !== mediaGeneration || surfaceId !== selectedSurfaceId || !primaryMedia) return;
      const outgoing = primaryImage;
      image.dataset.ensoMediaIncoming = "";
      primaryMedia.insertBefore(image, primaryFallback || null);
      if (primaryFallback) primaryFallback.hidden = true;
      primaryMedia.classList.remove("enso-object-product__media--empty");
      primaryMedia.dataset.ensoCurrentMediaId = item.mediaId;
      currentGallerySurfaceId = surfaceId;
      currentGalleryItemKey = item.mediaId;
      currentGalleryPosition = position;
      const gallery = runtimeGalleries.get(surfaceId);
      updateGalleryMeta(gallery, item, position);

      image.addEventListener("error", () => {
        if (surfaceId !== selectedSurfaceId || currentGalleryItemKey !== item.mediaId) return;
        primaryImageReady = false;
        gallery?.broken.add(item.mediaId);
        requestGallery(surfaceId, position);
      }, { once: true });

      if (reducedMotion.matches) {
        image.dataset.ready = "true";
        outgoing?.remove();
      } else {
        window.requestAnimationFrame(() => {
          image.dataset.ready = "true";
          if (outgoing) outgoing.dataset.ensoMediaOutgoing = "";
        });
        window.setTimeout(() => outgoing?.remove(), 420);
      }
      primaryImage = image;
      primaryImageReady = true;
    };

    const requestGallery = async (surfaceId, requestedPosition = 0) => {
      const generation = ++mediaGeneration;
      delete root.dataset.mediaPending;
      const gallery = runtimeGalleries.get(surfaceId);
      const items = visibleGalleryItems(gallery);
      if (!gallery || !items.length) {
        commitNeutralMedia(surfaceId, generation);
        return;
      }
      const position = ((requestedPosition % items.length) + items.length) % items.length;
      const item = items[position];
      if (surfaceId === currentGallerySurfaceId
        && item.mediaId === currentGalleryItemKey
        && primaryImage
        && primaryImageReady) {
        currentGalleryPosition = position;
        updateGalleryMeta(gallery, item, position, false);
        return;
      }
      root.dataset.mediaPending = String(generation);
      const image = await loadMediaItem(item);
      if (generation !== mediaGeneration || surfaceId !== selectedSurfaceId) {
        if (root.dataset.mediaPending === String(generation)) delete root.dataset.mediaPending;
        return;
      }
      if (root.dataset.mediaPending === String(generation)) delete root.dataset.mediaPending;
      if (!image) {
        gallery.broken.add(item.mediaId);
        requestGallery(surfaceId, position);
        return;
      }
      commitMedia(surfaceId, item, position, image, generation);
    };

    const rehydrateFromLocation = () => {
      const nextState = stateFromLocation();
      const surfaceChanged = nextState.surfaceId !== selectedSurfaceId;
      selectedSurfaceId = nextState.surfaceId;
      selectedSize = nextState.size;
      commitCommerce();
      if (surfaceChanged || currentGallerySurfaceId !== selectedSurfaceId) requestGallery(selectedSurfaceId, 0);
    };

    surfaceInputs.forEach((input) => {
      input.addEventListener("change", () => {
        const nextSurfaceId = normalizeId(input.dataset.ensoSurfaceId);
        if (!surfaceById.has(nextSurfaceId)) return;
        const surfaceChanged = nextSurfaceId !== selectedSurfaceId;
        selectedSurfaceId = nextSurfaceId;
        if (selectedSize && !exactVariant(selectedSurfaceId, selectedSize)) selectedSize = "";
        commitCommerce();
        if (surfaceChanged) requestGallery(selectedSurfaceId, 0);
      });
    });
    sizeInputs.forEach((input) => {
      input.addEventListener("change", () => {
        selectedSize = input.value;
        commitCommerce();
      });
    });
    fitInputs.forEach((input) => {
      input.addEventListener("change", () => {
        selectedFit = input.value;
        commitCommerce();
      });
    });

    galleryPrevious?.addEventListener("click", () => {
      requestGallery(selectedSurfaceId, currentGalleryPosition - 1);
    });
    galleryNext?.addEventListener("click", () => {
      requestGallery(selectedSurfaceId, currentGalleryPosition + 1);
    });
    primaryMedia?.addEventListener("touchstart", (event) => {
      const touch = event.changedTouches?.[0];
      if (touch) touchStart = { x: touch.clientX, y: touch.clientY };
    }, { passive: true });
    primaryMedia?.addEventListener("touchend", (event) => {
      const touch = event.changedTouches?.[0];
      if (!touch || !touchStart) return;
      const deltaX = touch.clientX - touchStart.x;
      const deltaY = touch.clientY - touchStart.y;
      touchStart = null;
      if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.25) return;
      requestGallery(selectedSurfaceId, currentGalleryPosition + (deltaX < 0 ? 1 : -1));
    }, { passive: true });

    copyContextButton?.addEventListener("click", async () => {
      const value = copyContextButton.dataset.ensoCopyValue || contextText(lastExact);
      let copied = false;
      try {
        if (navigator.clipboard?.writeText && window.isSecureContext) {
          await navigator.clipboard.writeText(value);
          copied = true;
        } else {
          const field = document.createElement("textarea");
          field.value = value;
          field.setAttribute("readonly", "");
          field.style.position = "fixed";
          field.style.opacity = "0";
          document.body.append(field);
          field.select();
          copied = document.execCommand("copy");
          field.remove();
        }
      } catch (error) {
        copied = false;
      }
      if (copied) {
        if (copyStatus) copyStatus.textContent = "CONFIGURATION COPIED";
        if (whatsappAction?.dataset.ensoWhatsappValid === "true") whatsappAction.hidden = false;
        if (manualContext) manualContext.hidden = true;
      } else {
        if (copyStatus) {
          copyStatus.textContent = "COPY UNAVAILABLE — COPY THE SELECTED DETAILS BELOW, THEN OPEN WHATSAPP";
        }
        if (whatsappAction?.dataset.ensoWhatsappValid === "true") whatsappAction.hidden = false;
        if (manualContext) {
          manualContext.value = value;
          manualContext.hidden = false;
          manualContext.focus({ preventScroll: true });
          manualContext.select();
        }
      }
    });

    form?.addEventListener("submit", (event) => {
      if (!variantInput?.value || addAction?.disabled || addAction?.hidden) {
        event.preventDefault();
        setError("Select one verified configuration before adding it to the bag.");
        productError?.focus({ preventScroll: true });
      }
    });

    window.addEventListener("popstate", rehydrateFromLocation);
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) rehydrateFromLocation();
    });

    configureWhatsapp();
    commitCommerce();
    requestGallery(selectedSurfaceId, 0);
  };

  const initializeInquiry = (root) => {
    if (root.dataset.ensoInitialized === "true") return;
    root.dataset.ensoInitialized = "true";

    const params = new URLSearchParams(window.location.search);
    const productTupleKeys = [
      "object", "handle", "surface", "size", "size_label", "fit", "variant",
      "mode", "source", "surface_required", "size_required"
    ];
    const contractParamKeys = [...productTupleKeys, "need"];
    const hasDuplicateContractParam = contractParamKeys.some((key) => params.getAll(key).length > 1);
    const need = cleanText(params.get("need"), 40);
    const studioNeed = need === "studio_conversation";
    const guidanceNeed = need === "configuration_guidance";
    const allowedNeed = inquiryModes.has(need) ? need : "";
    const mode = cleanText(params.get("mode"), 40);
    const rawSurfaceRequired = params.get("surface_required");
    const rawSizeRequired = params.get("size_required");
    const outerVariantValues = params.getAll("variant");
    const outerVariantPresent = outerVariantValues.length > 0;
    const requiredFlagsAreValid = params.has("surface_required")
      && params.has("size_required")
      && /^(0|1)$/.test(rawSurfaceRequired || "")
      && /^(0|1)$/.test(rawSizeRequired || "");
    const surfaceRequired = rawSurfaceRequired === "1";
    const sizeRequired = rawSizeRequired === "1";
    const values = {
      object: cleanText(params.get("object"), 120),
      handle: cleanText(params.get("handle"), 120).replace(/[^a-z0-9-]/gi, ""),
      surface: cleanText(params.get("surface"), 120),
      size: cleanText(params.get("size"), 80),
      sizeLabel: cleanText(params.get("size_label"), 80) || "Size",
      fit: cleanText(params.get("fit"), 120),
      variant: outerVariantValues.length === 1 ? strictNumericId(outerVariantValues[0]) : ""
    };
    const contextInput = root.querySelector("[data-enso-inquiry-context]");
    const sourceInput = root.querySelector("[data-enso-inquiry-source]");
    const typeInput = root.querySelector("[data-enso-inquiry-type]");
    const summary = root.querySelector("[data-enso-inquiry-summary]");
    const eyebrow = root.querySelector("[data-enso-inquiry-eyebrow]");
    const intro = root.querySelector("[data-enso-inquiry-intro]");
    const title = root.querySelector("[data-enso-inquiry-title]");
    const submit = root.querySelector("[data-enso-inquiry-submit]");
    const contextError = root.querySelector("[data-enso-inquiry-context-error]");
    const success = root.querySelector("[data-enso-inquiry-success]");
    const formError = root.querySelector("[data-enso-inquiry-errors]");

    if (success) {
      window.requestAnimationFrame(() => success.focus({ preventScroll: true }));
      return;
    }
    if (formError) window.requestAnimationFrame(() => formError.focus({ preventScroll: true }));

    const hasProductTuple = productTupleKeys.some((key) => params.has(key));
    if (studioNeed) {
      const studioIsValid = !hasDuplicateContractParam && !hasProductTuple;
      const studioCopy = cleanText(root.dataset.studioCopy, 320)
        || "For commissions, collaborations and selected creative projects, begin with a conversation.";
      if (eyebrow) eyebrow.textContent = root.dataset.studioEyebrow || "ENSO · STUDIO INQUIRY";
      if (title) title.textContent = root.dataset.studioTitle || "Start a conversation.";
      if (intro) {
        intro.textContent = studioCopy;
        intro.hidden = false;
      }
      if (contextInput) contextInput.value = "Inquiry type: Studio conversation";
      if (typeInput) typeInput.value = "Studio conversation";
      if (sourceInput) sourceInput.value = `${window.location.origin}${window.location.pathname}`;
      if (submit) submit.disabled = !studioIsValid;
      if (contextError) {
        contextError.hidden = studioIsValid;
        contextError.textContent = studioIsValid
          ? ""
          : "Studio inquiry context is malformed. Return to Home and start the conversation again.";
      }
      return;
    }

    let source = "";
    let sourceIsProduct = false;
    let sourceVariant = "";
    let sourceVariantPresent = false;
    const rawSource = params.get("source");
    if (rawSource) {
      try {
        const parsed = new URL(rawSource, window.location.origin);
        const productPath = `/products/${values.handle}`;
        const sourceVariantValues = parsed.searchParams.getAll("variant");
        const sourceVariantCount = sourceVariantValues.length;
        sourceVariantPresent = sourceVariantCount > 0;
        sourceVariant = sourceVariantCount === 1 ? strictNumericId(sourceVariantValues[0]) : "";
        if (parsed.origin === window.location.origin
          && parsed.pathname.endsWith(productPath)
          && sourceVariantCount <= 1
          && (!sourceVariantPresent || sourceVariant)) {
          source = parsed.href;
          sourceIsProduct = true;
        }
      } catch (error) {
        source = "";
      }
    }

    const summaryParts = [
      values.object,
      values.surface,
      values.size && `${values.sizeLabel} ${values.size}`,
      values.fit && `Fit ${values.fit}`,
      values.variant && `Variant ${values.variant}`
    ].filter(Boolean);
    const contextParts = [
      values.object && `Object: ${values.object}`,
      values.handle && `Handle: ${values.handle}`,
      values.surface && `Surface: ${values.surface}`,
      values.size && `${values.sizeLabel}: ${values.size}`,
      values.fit && `Fit: ${values.fit}`,
      values.variant && `Variant ID: ${values.variant}`,
      mode && `Fulfilment mode: ${mode}`,
      need && `Intent: ${need}`
    ].filter(Boolean);

    if (summary) {
      summary.textContent = summaryParts.join(" · ");
      summary.hidden = summaryParts.length === 0;
    }
    if (contextInput) contextInput.value = contextParts.join(" · ");
    if (sourceInput) sourceInput.value = source;

    if (guidanceNeed) {
      if (eyebrow) eyebrow.textContent = root.dataset.guidanceEyebrow || "ENSO · OBJECT GUIDANCE";
      if (title) title.textContent = root.dataset.guidanceTitle || "Ask Enso.";
      if (intro) {
        intro.textContent = root.dataset.guidanceCopy
          || "Share what you are considering and ENSO will help clarify surface, size or fit.";
        intro.hidden = false;
      }
      if (typeInput) typeInput.value = "Product configuration guidance";
      const variantPairIsValid = outerVariantPresent || sourceVariantPresent
        ? Boolean(outerVariantPresent
          && sourceVariantPresent
          && values.variant
          && sourceVariant
          && values.variant === sourceVariant)
        : true;
      const sizePairIsValid = values.size || params.has("size_label")
        ? Boolean(values.size && params.has("size_label") && cleanText(params.get("size_label"), 80))
        : true;
      const contextIsValid = Boolean(
        !hasDuplicateContractParam
        && requiredFlagsAreValid
        && values.object
        && values.handle
        && sourceIsProduct
        && (!surfaceRequired || values.surface)
        && sizePairIsValid
        && variantPairIsValid
      );
      if (submit) submit.disabled = !contextIsValid;
      if (contextError) {
        contextError.hidden = contextIsValid;
        contextError.textContent = contextIsValid
          ? ""
          : "Configuration context is incomplete. Return to the product and ask ENSO again.";
      }
      return;
    }

    if (typeInput) typeInput.value = allowedNeed === "personal_fit"
      ? "Personal-fit product inquiry"
      : allowedNeed === "request_availability"
        ? "Product availability inquiry"
        : "";
    if (title) {
      title.textContent = allowedNeed === "personal_fit"
        ? (root.dataset.personalFitTitle || "Begin a personal-fit inquiry.")
        : allowedNeed === "request_availability"
          ? (root.dataset.requestTitle || "Request availability.")
          : (root.dataset.defaultTitle || "Begin an inquiry.");
    }

    const modeMatchesIntent = allowedNeed === "personal_fit"
      ? mode === "personal_fit"
      : allowedNeed === "request_availability"
        ? ["request_availability", "ready_stock", "standard_mto"].includes(mode)
        : false;
    const contextIsValid = Boolean(
      !hasDuplicateContractParam
      && requiredFlagsAreValid
      && allowedNeed
      && modeMatchesIntent
      && values.object
      && values.handle
      && values.variant
      && sourceIsProduct
      && sourceVariant === values.variant
      && (!surfaceRequired || values.surface)
      && (!sizeRequired || values.size)
    );
    if (submit) submit.disabled = !contextIsValid;
    if (contextError) {
      contextError.hidden = contextIsValid;
      contextError.textContent = contextIsValid
        ? ""
        : "Exact object context is incomplete. Return to the product and choose a configuration again.";
    }
  };

  const initialize = (scope = document) => {
    if (scope.matches?.("[data-enso-object-product]")) initializeProduct(scope);
    if (scope.matches?.("[data-enso-object-inquiry]")) initializeInquiry(scope);
    scope.querySelectorAll?.("[data-enso-object-product]").forEach(initializeProduct);
    scope.querySelectorAll?.("[data-enso-object-inquiry]").forEach(initializeInquiry);
  };

  initialize();
  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
  document.addEventListener("shopify:section:unload", () => {
    if (!document.querySelector("[data-enso-policy-dialog][open]")) {
      document.documentElement.classList.remove("enso-policy-dialog-open");
    }
  });
})();
